/**
 * `createStudioRuntimeBridge`, adapts the live `Studio` singleton to
 * the `RuntimeBridge` contract consumed by `RangeRunner`.
 *
 * Why this lives in `hooks/journey/` and not in `lib/learning-journey/`:
 * the lib must stay pure TS, free of any `lib/studio/*` import. The
 * bridge bolts on top of Studio and is therefore part of the React/runtime
 * adapter layer.
 *
 * Concerns covered here:
 *
 *   - `seek`              → `LocalTransport.seekToTick` (forward-aware seek).
 *   - `setPlaybackRate`   → `LocalTransport.setPlaybackRate`.
 *   - `start`             → `seekToTick` + `play` (no count-in path).
 *   - `startWithCountIn`  → `Studio.startWithCountIn` (forwards `skipSectionTips`).
 *   - `pause`             → `LocalTransport.pause`.
 *   - `onTick`            → `requestAnimationFrame` loop reading
 *                           `LocalTransport.currentTick()`. A native
 *                           tick-stream API would be a welcome chantier
 *                           but is not required for V1.
 *   - `cancelCountIn`     → `Studio.countIn.cancel`.
 *   - `probe`             → audio clock, audio state and playhead tick,
 *                           for the runner's watchdog.
 *
 * The factory returns a plain object; no class is needed, the bridge has
 * no internal state of its own (cancellation flags etc. are owned by the
 * `RangeRunner`).
 */

import type { Studio } from '@/lib/studio/studio';
import type { RuntimeBridge, RuntimeProbe } from '@/lib/learning-journey/runtime/runtime-bridge';

/**
 * Adapts a live `Studio` to the `RuntimeBridge` contract.
 *
 * The `Studio` reference is captured by closure; the caller is responsible
 * for ensuring the bridge is not used after `Studio.destroy()`.
 */
export function createStudioRuntimeBridge(studio: Studio): RuntimeBridge {
  return {
    seek(tick: number): void {
      studio.getLocalTransport().seekToTick(tick);
    },

    setPlaybackRate(rate: number): void {
      studio.getLocalTransport().setPlaybackRate(rate);
    },

    start(fromTick: number): void {
      const lt = studio.getLocalTransport();
      lt.seekToTick(fromTick);
      lt.play();
    },

    startWithCountIn(
      fromTick: number,
      opts?: { skipSectionTips?: boolean },
    ): Promise<void> {
      return studio.startWithCountIn(fromTick, opts);
    },

    pause(): void {
      studio.getLocalTransport().detach();
    },

    onTick(cb: (tick: number) => void): () => void {
      const lt = studio.getLocalTransport();
      let cancelled = false;
      let rafId = 0;

      const tick = () => {
        if (cancelled) {
          return;
        }

        cb(lt.currentTick());
        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);

      return () => {
        if (cancelled) {
          return;
        }

        cancelled = true;
        cancelAnimationFrame(rafId);
      };
    },

    cancelCountIn(): void {
      studio.countIn.cancel();
    },

    probe(): RuntimeProbe {
      const ctx = studio.getAudioContext();

      return {
        audioTime: ctx.currentTime,
        // Safari's `interrupted` is not in the DOM type; read it as it comes.
        audioState: String(ctx.state),
        tick: studio.getLocalTransport().currentTick(),
      };
    },
  };
}
